import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  formatDuration,
  formatRelative,
  formatViewCount,
  useAdminYoutubeVideos,
  useDeleteVideo,
  videoWatchUrl,
} from "../../../lib/youtube";
import { useCan } from "../../../lib/auth";
import { YoutubePlayer } from "../../../components/youtube/YoutubePlayer";
import { Link as RouterLink } from "react-router-dom";

/**
 * Admin videos catalog. Search, filter by privacy, paginate. Each row
 * has edit / preview / open / delete affordances; delete is gated by
 * `youtube.delete` and uses a typed-confirmation modal pattern.
 */

const PAGE_SIZE = 25;

export default function YouTubeVideos() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const privacy = params.get("privacy") ?? "";
  const offset = Math.max(0, Number(params.get("offset") ?? "0"));

  const [qInput, setQInput] = useState(q);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    // any filter change resets pagination
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("q", qInput.trim() || null);
  };

  const queryRes = useAdminYoutubeVideos({ limit: PAGE_SIZE, offset, q, privacy });
  const data = queryRes.data;
  const videos = data?.videos ?? [];
  const total = data?.total ?? 0;
  const isLoading = queryRes.isLoading;
  const canDelete = useCan("youtube.delete");

  return (
    <div>
      <div className="yt-admin-toolbar">
        <form onSubmit={onSearch} className="yt-search">
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search titles or IDs…"
            aria-label="Search videos"
          />
          <button type="submit" className="btn-app-ghost">
            Search
          </button>
        </form>

        <div className="flex items-center gap-2 flex-wrap">
          {(["", "public", "unlisted", "private"] as const).map((p) => (
            <button
              key={p || "all"}
              type="button"
              className={`chip ${privacy === p ? "is-active" : ""}`}
              onClick={() => setParam("privacy", p || null)}
            >
              {p === "" ? "All" : p}
            </button>
          ))}
        </div>

        <Link
          to="/dashboard/youtube/upload"
          className="btn-app yt-toolbar-cta"
        >
          + Upload video
        </Link>

        <p className="yt-admin-count">
          {isLoading ? (
            "Loading…"
          ) : (
            <>
              <span className="tabular">{Math.min(offset + 1, total)}</span>
              <span> – </span>
              <span className="tabular">{Math.min(offset + videos.length, total)}</span>
              <span> of </span>
              <span className="tabular">{total}</span>
            </>
          )}
        </p>
      </div>

      {isLoading ? (
        <p className="yt-admin-empty mt-6">Loading videos…</p>
      ) : videos.length === 0 ? (
        <p className="yt-admin-empty mt-6">
          {q || privacy
            ? "No videos match these filters."
            : "Nothing here — run a sync to populate."}
        </p>
      ) : (
        <ul className="yt-row-list mt-4">
          {videos.map((v) => (
            <VideoRow key={v.id} v={v} canDelete={canDelete} />
          ))}
        </ul>
      )}

      <Pagination
        offset={offset}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={(o) => setParam("offset", o > 0 ? String(o) : null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function VideoRow({
  v,
  canDelete,
}: {
  v: {
    id: string;
    title: string;
    thumbnailUrl: string;
    publishedAt: string | null;
    durationSec: number | null;
    privacyStatus: string | null;
    viewCount: number | null;
    syncedAt: string | null;
  };
  canDelete: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const del = useDeleteVideo();
  const isPublic = v.privacyStatus === "public";
  const matchesTitle = confirmInput.trim() === v.title.trim();

  return (
    <li className="yt-row">
      <button
        type="button"
        className="yt-row-thumb"
        onClick={() => setExpanded((e) => !e)}
        aria-label={expanded ? "Collapse preview" : "Preview video"}
        aria-expanded={expanded}
      >
        <img src={v.thumbnailUrl} alt="" loading="lazy" decoding="async" />
        <span className="yt-row-thumb-dur tabular" aria-hidden>
          {formatDuration(v.durationSec)}
        </span>
      </button>

      <div className="yt-row-body">
        <RouterLink
          to={`/dashboard/youtube/videos/${v.id}`}
          className="yt-row-title"
        >
          {v.title}
        </RouterLink>
        <p className="yt-row-meta">
          <span
            className={`yt-row-privacy ${isPublic ? "is-public" : "is-private"}`}
          >
            {v.privacyStatus ?? "?"}
          </span>
          <span className="yt-row-sep">·</span>
          <span>{formatRelative(v.publishedAt)}</span>
          <span className="yt-row-sep">·</span>
          <span>{formatViewCount(v.viewCount)} views</span>
          <span className="yt-row-sep">·</span>
          <span className="font-grotesk text-[0.62rem]">{v.id}</span>
        </p>
        {expanded && (
          <div className="yt-row-embed">
            <YoutubePlayer kind="video" id={v.id} title={v.title} />
          </div>
        )}
        {confirmingDelete && (
          <div className="yt-danger-confirm" role="alert">
            <p>
              Type the video title{" "}
              <span className="font-display-italic">"{v.title}"</span> to
              confirm deletion. This cannot be undone.
            </p>
            <div className="flex gap-2 items-center mt-2">
              <input
                type="text"
                className="auth-input"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Type the title to confirm…"
                autoFocus
              />
              <button
                type="button"
                className="btn-app is-danger"
                disabled={!matchesTitle || del.isPending}
                onClick={() => del.mutate(v.id)}
              >
                {del.isPending ? "Deleting…" : "Confirm delete"}
              </button>
              <button
                type="button"
                className="btn-app-ghost"
                onClick={() => {
                  setConfirmingDelete(false);
                  setConfirmInput("");
                }}
                disabled={del.isPending}
              >
                Cancel
              </button>
            </div>
            {del.error && (
              <p className="yt-admin-error mt-2">{del.error.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="yt-row-actions">
        <RouterLink
          to={`/dashboard/youtube/videos/${v.id}`}
          className="row-action"
        >
          Edit
        </RouterLink>
        <a
          href={videoWatchUrl(v.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="row-action"
        >
          Open
        </a>
        {canDelete && !confirmingDelete && (
          <button
            type="button"
            className="row-action is-danger"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </button>
        )}
      </div>
    </li>
  );
}

function Pagination({
  offset,
  pageSize,
  total,
  onChange,
}: {
  offset: number;
  pageSize: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  if (total <= pageSize) return null;
  const canPrev = offset > 0;
  const canNext = offset + pageSize < total;
  return (
    <nav className="yt-pagination" aria-label="Pagination">
      <button
        type="button"
        className="btn-app-ghost"
        onClick={() => onChange(Math.max(0, offset - pageSize))}
        disabled={!canPrev}
      >
        ← Prev
      </button>
      <button
        type="button"
        className="btn-app-ghost"
        onClick={() => onChange(offset + pageSize)}
        disabled={!canNext}
      >
        Next →
      </button>
    </nav>
  );
}
